import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useJoyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useNavigate } from 'react-router';
import { useMutation, useApolloClient } from '@apollo/client';
import { Box, Button, Typography, Card, CardContent, LinearProgress } from '@mui/joy';
import { COMPLETE_ONBOARDING } from '../../gql/onboarding.mutations';
import {
  FIRST_STEP_INDEX,
  JOYRIDE_STEPS,
  LAST_STEP_INDEX,
  TARGET_POLL_INTERVAL_MS,
  TOOLTIP_CLASS_NAME,
  resolveStepDestination,
} from './onboardingTourConfig';

const ACTIVE_STATUSES = new Set([STATUS.READY, STATUS.RUNNING, STATUS.PAUSED]);

function JoyTooltip({ backProps, continuous, index, isLastStep, primaryProps, size, step, tooltipProps }) {
  return (
    <Card
      {...tooltipProps}
      className={[TOOLTIP_CLASS_NAME, tooltipProps.className].filter(Boolean).join(' ')}
      variant="outlined"
      sx={{
        maxWidth: 320,
        boxShadow: 'lg',
        border: '1.5px solid',
        borderColor: 'primary.200',
        borderRadius: 'lg',
        p: 0,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {step.title && (
          <Typography level="title-md" sx={{ mb: 1 }}>
            {step.title}
          </Typography>
        )}
        <Typography level="body-sm">{step.content}</Typography>
      </CardContent>

      <Box sx={{ px: 2, pb: 1.5 }}>
        <LinearProgress
          determinate
          value={((index + 1) / size) * 100}
          color="primary"
          size="sm"
          sx={{ mb: 1.5, borderRadius: 'xl' }}
        />
        <Box sx={{ display: 'flex', justifyContent: index > 0 ? 'space-between' : 'flex-end', alignItems: 'center' }}>
          {index > 0 && (
            <Button {...backProps} variant="plain" color="neutral" size="sm">
              Back
            </Button>
          )}
          {continuous && (
            <Button {...primaryProps} variant="solid" color="primary" size="sm">
              {isLastStep ? 'Done' : 'Next'}
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  );
}

export const OnboardingContext = createContext({
  isTourActive: false,
  tourStepIndex: 0,
  advanceTour: () => {},
  setFirstApplicationData: () => {},
});

export const useOnboarding = () => useContext(OnboardingContext);

const OnboardingTour = ({ user, children }) => {
  const navigate = useNavigate();
  const client = user?.client;
  const [completeOnboarding] = useMutation(COMPLETE_ONBOARDING);
  const apolloClient = useApolloClient();
  const firstJobRef = useRef(null);
  const firstApplicationDataRef = useRef(null);
  const controlsRef = useRef(null);
  const waitTokenRef = useRef(0);
  const startedClientRef = useRef(null);
  const completionStartedRef = useRef(false);

  const { controls, on, state, Tour } = useJoyride({
    steps: JOYRIDE_STEPS,
    continuous: true,
    tooltipComponent: JoyTooltip,
    options: {
      primaryColor: '#3b3bcd',
      overlayColor: 'rgba(0, 0, 0, 0.4)',
      zIndex: 2000,
      overlayClickAction: false,
      dismissKeyAction: false,
      blockTargetInteraction: true,
    },
  });

  // Keep ref current so all callbacks always have the latest controls object
  controlsRef.current = controls;

  useEffect(() => {
    completionStartedRef.current = false;
  }, [client?._id]);

  const cancelPendingTargetWait = useCallback(() => {
    waitTokenRef.current += 1;
  }, []);

  const markComplete = useCallback(async () => {
    if (completionStartedRef.current) return;

    completionStartedRef.current = true;
    cancelPendingTargetWait();
    controlsRef.current?.stop();
    try {
      console.log("Marking onboarding complete for client", client?._id);
      await completeOnboarding();
      if (client?._id) {
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({ _id: client._id, __typename: 'Client' }),
          fields: { onboardingCompleted: () => true },
        });
      }
    } catch (e) {
      completionStartedRef.current = false;
      console.error('Failed to mark onboarding complete', e);
    }
  }, [apolloClient, cancelPendingTargetWait, client?._id, completeOnboarding]);

  const startStepWhenTargetIsReady = useCallback(({ selector, stepIndex }) => {
    cancelPendingTargetWait();
    const waitToken = waitTokenRef.current;
    const check = () => {
      if (waitToken !== waitTokenRef.current) return;
      if (document.querySelector(selector)) {
        const c = controlsRef.current;
        if (!c) {
          setTimeout(check, TARGET_POLL_INTERVAL_MS);
          return;
        }
        c.start(stepIndex);
      } else {
        setTimeout(check, TARGET_POLL_INTERVAL_MS);
      }
    };
    setTimeout(check, TARGET_POLL_INTERVAL_MS);
  }, [cancelPendingTargetWait]);

  const navigateToStep = useCallback((stepIndex) => {
    if (!controlsRef.current) return;

    const destination = resolveStepDestination(stepIndex, {
      firstJob: firstJobRef.current,
      appData: firstApplicationDataRef.current,
    });

    navigate(destination.path);
    startStepWhenTargetIsReady(destination);
  }, [navigate, startStepWhenTargetIsReady]);

  useEffect(() => {
    if (!client?._id) return;
    if (client.onboardingCompleted) return;
    if ((client.jobs || []).filter(x => !x.title?.startsWith("[DEMO")).length > 0) return;
    if (startedClientRef.current === client._id) return;

    startedClientRef.current = client._id;
    firstJobRef.current = [...client.jobs].reverse()[0];
    navigateToStep(FIRST_STEP_INDEX);
  }, [client, navigateToStep]);

  useEffect(() => {
    const unsubStep = on('step:after', (data) => {
      const { action, index } = data;
      const c = controlsRef.current;
      if (!c) return;

      if (action === ACTIONS.PREV) {
        c.stop();
        navigateToStep(index - 1);
        return;
      }

      if (index >= LAST_STEP_INDEX) {
        markComplete();
        return;
      }

      c.stop();
      navigateToStep(index + 1);
    });

    const unsubNotFound = on(EVENTS.TARGET_NOT_FOUND, (data) => {
      if (data.index >= LAST_STEP_INDEX) {
        markComplete();
        return;
      }

      controlsRef.current?.stop();
      navigateToStep(data.index + 1);
    });

    const unsubEnd = on('tour:end', markComplete);

    return () => {
      unsubStep();
      unsubNotFound();
      unsubEnd();
    };
  }, [markComplete, navigateToStep, on]);

  const advanceTour = useCallback(() => {
    controlsRef.current?.next();
  }, []);

  const setFirstApplicationData = useCallback((jobId, appId) => {
    firstApplicationDataRef.current = { jobId, appId };
  }, []);

  const isTourActive = ACTIVE_STATUSES.has(state.status);
  const contextValue = useMemo(() => ({
    isTourActive,
    tourStepIndex: state.index,
    advanceTour,
    setFirstApplicationData,
  }), [advanceTour, isTourActive, setFirstApplicationData, state.index]);

  return (
    <OnboardingContext.Provider value={contextValue}>
      {Tour}
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingTour;
